import { useMemo } from 'react'

import { cn } from '@fortem-monorepo/js-utilities'
import { lerpColorRGBA } from '@fortem-monorepo/js-utilities'
import { groupByKey } from '@fortem-monorepo/js-utilities'
import { Separator } from '@fortem-monorepo/react-ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@fortem-monorepo/react-ui'

import { ICReportChartPrimitive } from '@/pages/funds/ic-report/components/primitives/types'
import { protection } from '@/react-query/queries/ic-report/hooks'

const renderArray = ({ min, max, interval }: { min: number; max: number; interval: number }) => {
  return Array.from({ length: (max - min) / interval + 1 }, () =>
    Array.from({ length: interval }),
  ) as undefined[][]
}

const getGroupedData = (input: any[], dataHandle: string) => {
  // Reduce the array down into unique values
  const reduced = input.reduce((prev, curr) => {
    const adjustedBarrierLevel = parseFloat((curr[dataHandle] as number).toFixed(2)) * 100

    const item = prev.find((el: any) => {
      return el.region_wrapper === curr.region_wrapper && el[dataHandle] === adjustedBarrierLevel
    })

    if (!item) prev.push({ ...curr, total: 1, [dataHandle]: adjustedBarrierLevel })
    else item.total++
    return prev
  }, [])

  const minTotal = Math.min(...reduced.map((item: any) => item.total))
  const maxTotal = Math.max(...reduced.map((item: any) => item.total))

  const final: any[] = reduced.map((item: any) => ({ ...item, minTotal, maxTotal }))

  return final
}

export interface BarriersChartProps extends ICReportChartPrimitive {
  minValue?: number
  maxValue?: number
  interval?: number
  data: any[]
  dataHandle: string
}

export const BarriersChart: React.FC<BarriersChartProps> = ({
  minValue = 0,
  maxValue = 55,
  interval = 5,
  data,
  dataHandle,
  params,
}) => {
  const protectionOptionsQuery = protection.useOptions(params)

  const moneynessDistances = useMemo(() => {
    return (protectionOptionsQuery.data ?? []).map((option) =>
      Math.round(option.moneyness_distance * 100),
    )
  }, [protectionOptionsQuery.data])

  const formattedData = useMemo(() => {
    return getGroupedData(data, dataHandle)
  }, [data])

  const groupedData = useMemo(() => groupByKey(formattedData, 'region_wrapper'), [formattedData])
  const gridLines = useMemo(
    () => renderArray({ min: minValue, max: maxValue, interval }),
    [minValue, maxValue, interval],
  )

  return (
    <div className={'w-full p-4'}>
      {gridLines.map((intervalArray, intervalIndex) => {
        return (
          <div key={intervalIndex} className={cn('ml-10', {})}>
            <div className={'flex-1 flex flex-col'}>
              {intervalArray.map((_, gridLineIndex) => {
                const label = intervalIndex * interval + gridLineIndex
                const withLabel = label % interval === 0
                const hasMoneynessLine = moneynessDistances.includes(label)

                return (
                  <div
                    key={gridLineIndex}
                    className={
                      'relative has-[.datapoint:hover]:[&>.gridline]:bg-gray-400 py-[2.5px]'
                    }
                  >
                    {/* Gridline Label */}
                    {withLabel && (
                      <span
                        className={
                          'text-sm text-muted-foreground text-right font-light w-[4ch] absolute -left-[5ch] top-1/2 -translate-y-1/2'
                        }
                      >
                        {label}%
                      </span>
                    )}
                    {/* Actual Gridline Content */}
                    <div
                      className={cn('gridline h-px transition-colors group-hover:bg-gray-400', {
                        ['border border-dashed border-green-600/70']: hasMoneynessLine,
                        ['bg-gray-200']: withLabel,
                      })}
                    />
                    {/* Each series items */}
                    <div className={'absolute left-0 top-0 w-full h-full flex justify-evenly'}>
                      {Object.entries(groupedData).map(([region, data]) => {
                        const foundData = data.find((item) => {
                          return item.barrier_level === label
                        })

                        return (
                          <div className={'relative flex justify-center'}>
                            <span className={'text-sm font-medium invisible'}>{region}</span>
                            <div className={'absolute top-1/2 left-1/2 -translate-1/2 h-px w-9'}>
                              {foundData && (
                                <Tooltip>
                                  <TooltipTrigger asChild className={'datapoint'}>
                                    <div
                                      style={{
                                        zIndex: foundData.total,
                                        backgroundColor: lerpColorRGBA(
                                          foundData.total,
                                          foundData.minTotal,
                                          foundData.maxTotal,
                                          '#d3e0e5',
                                          '#035869',
                                        ),
                                      }}
                                      className={
                                        'absolute top-1/2 -translate-y-1/2 left-0 w-full h-1.5 rounded-[1.5px]'
                                      }
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent className={'pointer-event-none'}>
                                    <div className="flex flex-col gap-1 text-sm">
                                      <div className="flex gap-2 items-center">
                                        <span className={'font-medium'}>{region}</span>
                                      </div>
                                      <Separator />
                                      <div className="flex items-center gap-4 justify-between">
                                        <span>Barrier Level</span>
                                        <span>{foundData.barrier_level}%</span>
                                      </div>
                                      <div className="flex items-center gap-4 justify-between">
                                        <span>Total</span>
                                        <span>{foundData.total}</span>
                                      </div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
      <div className={'ml-10 flex justify-evenly'}>
        {Object.keys(groupedData).map((region) => {
          return (
            <span key={region} className={'text-sm font-medium text-primary/60'}>
              {region}
            </span>
          )
        })}
      </div>
    </div>
  )
}
